<script>
  import { InlineNotification, Modal } from 'carbon-components-svelte';
  import { createEventDispatcher } from 'svelte';
  import startCase from 'lodash/startCase';

  import LoadingSpinner from './LoadingSpinner.svelte';

  import { getServerErrorMessage } from '../utils';

  export let open = false,
    entityType = 'Entity',
    entityData = {},
    entityDisplayName = '',
    handler = null;

  const dispatch = createEventDispatcher();
  const keysToIgnore = ['createdAt', 'updatedAt', 'deletedAt', 'createdBy', 'updatedBy'];
  const action = `delete-${entityType.toLowerCase().replace(/\s+/g, '-')}`;

  let resultNotification = null,
    loading = false;

  async function handleDelete() {
    if (handler) {
      resultNotification = null;
      loading = true;

      try {
        await handler(entityData.id);
        dispatchDeleteEvent();
      } catch (error) {
        const errorMessage = getServerErrorMessage(error) ?? 'Verify your submission and try again!';
        console.error(`[DeleteEntityModal] Failed to delete ${entityType}! Server says: ${errorMessage}`, error);
        resultNotification = `Failed to delete ${entityType}! ${errorMessage}`;
      } finally {
        loading = false;
      }
      return;
    }

    dispatchDeleteEvent();
  }

  function handleClose() {
    dispatch('close');
    open = false;
  }

  function dispatchDeleteEvent() {
    dispatch(action, { action, data: entityData });
  }

  $: propertiesToShow = Object.fromEntries(
    Object.entries(entityData).filter(
      ([key, value]) => value !== undefined && value !== null && value !== '' && typeof value !== 'object' && !keysToIgnore.includes(key)
    )
  );
</script>

<Modal
  danger
  {open}
  modalHeading="Delete {entityType}"
  modalLabel={entityDisplayName || 'Confirm Deletion'}
  primaryButtonText="Delete"
  secondaryButtonText="Cancel"
  on:submit={handleDelete}
  on:close={handleClose}
  on:click:button--secondary={handleClose}
>
  <LoadingSpinner {loading}>
    {#if resultNotification}
      <InlineNotification lowContrast hideCloseButton kind="error" subtitle={resultNotification} />
    {/if}
    <div class="entity-details">
      {#each Object.entries(propertiesToShow) as [key, value]}
        <div class="detail-row">
          <strong>{startCase(key)}:</strong>
          <span>{value}</span>
        </div>
      {/each}
    </div>
    {#if Object.entries(propertiesToShow).length === 0}
      <p class="no-details">No additional details available.</p>
    {/if}
  </LoadingSpinner>
</Modal>

<style>
  .entity-details {
    margin-top: 1rem;
    padding: 1rem;
    border-left: 4px solid var(--cds-danger);
  }

  .detail-row {
    display: flex;
    margin-bottom: 0.5rem;
    word-break: break-word;
  }

  .detail-row:last-child {
    margin-bottom: 0;
  }

  .detail-row strong {
    min-width: 120px;
    margin-right: 1rem;
    color: var(--cds-text-01);
  }

  .detail-row span {
    color: var(--cds-text-02);
    flex: 1;
  }

  .no-details {
    color: var(--cds-text-02);
    font-style: italic;
  }
</style>
